import firebase from 'firebase/app';
import 'firebase/firestore';
import 'firebase/database';
import _ from 'lodash';
import {globalfunc} from '../globalfunc.js';
import { Result } from 'element-ui';
import { getTeamByCompany } from './core/cacheService';

function normalizeOrganizationTree(organization: any, teamMaster: any) {
  const organization1level: any = {}

  _.forEach(organization, (scope: any, teamId: string) => {
    organization1level[teamId] = {}
    _.forEach(scope, (_value: any, key: string) => {
      organization1level[teamId][key] = true
    })
    organization1level[teamId][teamId] = true
  })

  _.forEach(teamMaster, (_team: any, teamId: string) => {
    if (!organization1level[teamId]) {
      organization1level[teamId] = {}
    }
    organization1level[teamId][teamId] = true
  })

  return organization1level
}

function mapTeamsByKey(teams: any[]) {
  const teamMaster: any = {}

  _.forEach(teams, (team: any) => {
    if (team && team.key) {
      teamMaster[team.key] = team
    }
  })

  return teamMaster
}

// Helper functions for management distance calculation
function buildManagementChain(
  userId: string,
  userMaster: any,
  chainCache: Record<string, string[]>,
  maxDepth = 10
): string[] {
  if (chainCache[userId]) {
    return chainCache[userId]
  }

  const chain: string[] = []
  const visited = new Set<string>()
  let currentUserId = userId
  let depth = 0

  while (currentUserId && depth < maxDepth) {
    if (visited.has(currentUserId)) {
      console.warn(`Circular reporting relationship detected: ${userId}`)
      break
    }

    visited.add(currentUserId)
    chain.push(currentUserId)

    const user = userMaster[currentUserId]
    if (!user || !user.boss) {
      break
    }

    currentUserId = user.boss.replace(/\./g, "||")
    depth++
  }

  chainCache[userId] = chain
  return chain
}

function findManagementDistance(
  userId1: string,
  userId2: string,
  userMaster: any,
  chainCache: Record<string, string[]>,
  distanceCache: Record<string, number>
): number {
  if (userId1 === userId2) return 0

  const cacheKey = userId1 < userId2 ? `${userId1}__${userId2}` : `${userId2}__${userId1}`
  if (distanceCache[cacheKey] !== undefined) {
    return distanceCache[cacheKey]
  }

  const chain1 = buildManagementChain(userId1, userMaster, chainCache)
  const chain2 = buildManagementChain(userId2, userMaster, chainCache)
  const chain2Index: Record<string, number> = {}

  for (let i = 0; i < chain2.length; i++) {
    if (chain2Index[chain2[i]] === undefined) {
      chain2Index[chain2[i]] = i
    }
  }

  let minDistance = Infinity
  for (let i = 0; i < chain1.length; i++) {
    const matchIndex = chain2Index[chain1[i]]
    if (matchIndex !== undefined) {
      minDistance = Math.min(minDistance, i + matchIndex)
    }
  }

  distanceCache[cacheKey] = minDistance === Infinity ? 999 : minDistance
  return distanceCache[cacheKey]
}

// Compute power rating once per user to avoid heavy recomputations in the component
function calculatePowerRating(row: any): number {
  if (!row || typeof row !== 'object') return 0;
  const strongCount = Number(row.strong_count) || 0;
  const totalCount = Number(row.total_count) || 0;
  const innerCount = Number(row.inner_count) || 0;
  const meRatio = Number(row.me_ratio) || 0;

  let score = 0;

  // Strong connections quality (40%) with diminishing returns
  let strongQualityScore = 0;
  if (strongCount === 0) strongQualityScore = 0;
  else if (strongCount >= 1 && strongCount <= 2) strongQualityScore = 0.6;
  else if (strongCount >= 3 && strongCount <= 10) strongQualityScore = 1;
  else strongQualityScore = Math.min(1, 0.85 + (0.15 * Math.log(strongCount) / Math.log(20)));
  score += strongQualityScore * 40;

  // Network position via signatures (30%)
  let positionScore = 0;
  if (row.signatures && row.signatures.length > 0) {
    const signatureScores: Record<string, number> = {
      Hub: 1.0, Influence: 1.0, Brokerage: 1.0, Strategist: 1.0,
      'Community Leader': 0.8, Collective: 0.8, Linker: 0.8,
      Connectivity: 0.6, Reachability: 0.6, Ripple: 0.6,
      Cohesion: 0.4,
      Insular: -0.5, Peripheral: -0.5, Distant: -0.5,
    };
    let maxPositive = 0;
    let penalty = 0;
    row.signatures.forEach((sig: any) => {
      const s = signatureScores[sig.name] || 0;
      if (s > 0) maxPositive = Math.max(maxPositive, s); else penalty += s;
    });
    positionScore = Math.max(0, maxPositive + penalty);
  }
  score += positionScore * 30;

  // Connection efficiency (15%) with diminishing returns after 30
  let efficiencyScore = 0;
  if (totalCount > 0) {
    efficiencyScore = Math.min(1, Math.log(totalCount + 1) / Math.log(31));
  }
  score += efficiencyScore * 15;

  // Network diversity (10%): outer vs inner balance
  let diversityScore = 0;
  if (totalCount > 0) {
    const outerCount = Math.max(0, totalCount - innerCount);
    let bridging = 0; // outer
    if (outerCount >= 4) bridging = 1; else if (outerCount >= 2) bridging = 0.8; else if (outerCount >= 1) bridging = 0.5;
    let bonding = 0; // inner
    if (innerCount >= 5) bonding = 1; else if (innerCount >= 3) bonding = 0.8; else if (innerCount >= 1) bonding = 0.5;
    diversityScore = (bridging * 0.6) + (bonding * 0.4);
  }
  score += diversityScore * 10;

  // Manager support (5%): me_ratio closeness to 1.0
  let managerScore = 0;
  if (meRatio >= 0) {
    if (meRatio >= 0.8 && meRatio <= 1.2) managerScore = 1;
    else if (meRatio >= 0.5 && meRatio <= 1.5) managerScore = 0.7;
    else if (meRatio >= 0.2 && meRatio <= 2.0) managerScore = 0.5;
    else managerScore = 0.3;
  }
  score += managerScore * 5;

  // Scaling multiplier for small networks
  let scaling = 1.0;
  if (totalCount < 5) scaling = 0.7; else if (totalCount < 10) scaling = 0.85;

  score = score * scaling;
  return Math.round(Math.min(score, 100));
}
export default {
  namespaced: true,
  state: {
    page: 1,
    size_page: 10,
    SortField: "",
    SortOrder: "",
    team_id: '',
    company_id: '',
    selected_nodes: 'initials_team',
    selected_attribute: 'impact_rating',
    selected_links: 'all_relational_data',
    selected_email: '',  // Email of selected user for focus mode
    loadingRelationshipsTabs: true,
    organization1level: {},
    dataAllAttribute: {},
    teamMembersIndex: {},
    datalist: [],
    datalistAll: [],
    _datalistAll:[],
    dataTableBuildToken: 0,
    signatures:[
      {
        id: 'brokerage',
        name: 'Brokerage',
        icon: 'sig_brokerage',
        tooltip: 'Brokerage indicates that an individual frequently lies on the shortest path between other individuals in the network. This suggests that the individual plays a significant role as a bridge or broker, connecting disparate groups and potentially controlling the flow of information or resources.',
        color:''
      },{
        id: 'cohesion',
        name: 'Cohesion',
        icon: 'sig_cohesion',
        tooltip: 'Cohesion measures how an individual\'s connections are connected, forming tight-knit groups or clusters. It indicates that an individual\'s connections are likely connected, forming tight-knit groups or clusters. This suggests strong relationships and high levels of collaboration within the individual\'s local network.',
        color:''
      },{
        id: 'connectivity',
        name: 'Connectivity',
        icon: 'sig_connectivity',
        tooltip: 'Connectivity indicates that an individual has high direct connections within the network. This suggests that the individual is well-connected, which can provide them with access to information, resources, and opportunities.',
        color:''
      },{
        id: 'collective',
        name: 'Collective',
        icon: 'sig_collective',
        tooltip: '‌Collective indicates that a person is central and important in the network. These individuals are well-connected, can reach others more quickly, and may have a higher influence on the flow of information in the network.',
        color:''
      },{
        id: 'influence',
        name: 'Influence',
        icon: 'sig_influence',
        tooltip: '‌Influence indicates that an individual is not only well-connected but also connected to other influential individuals. This means that they might have significant influence within the network and can access diverse information through their connections.',
        color:''
      },{
        id: 'reachability',
        name: 'Reachability',
        icon: 'sig_reachability',
        tooltip: 'Reachability indicates that an individual is well-positioned to spread information or influence others efficiently within the network. The individual is, on average, close to all other individuals in the network, which may provide them with better access to information, resources, and opportunities.',
        color:''
      },
      //FIX code before release
      {
        id: 'influenceI',
        name: 'Collective',
        icon: 'sig_collective',
        tooltip: '‌Collective indicates that a person is central and important in the network. These individuals are well-connected, can reach others more quickly, and may have a higher influence on the flow of information in the network.',
        color:''
      },{
        id: 'influenceII',
        name: 'influence',
        icon: 'sig_influence',
        tooltip: '‌Influence indicates that an individual is not only well-connected but also connected to other influential individuals. This means that they might have significant influence within the network and can access diverse information through their connections.',
        color:''
      },
      {
        id: 'insularity',
        name: 'Insular',
        icon: 'sig_insularity',
        tooltip: 'Insular indicates that an individual works primarily within their own team silo, with very limited connections outside their immediate group. This suggests they may be missing valuable opportunities for cross-team collaboration and broader organizational impact.',
        criterion: 0.75,
        color:'-grey'
      },
      {
        id: 'ripple',
        name: 'Ripple',
        icon: 'sig_ripple',
        tooltip: 'Ripple indicates the relative influence this person has by taking into account their direct relationships and how members of the network connect to them through these relationships.',
        color:''
      },
      {
        id: 'hub',
        name: 'Hub',
        icon: 'sig_hub',
        tooltip: 'A Hub is an individual who is both well-connected and can reach others in the network quickly. They serve as a central connecting point in the network.',
        criterion:0.90,
        color:'-blue'
      },
      {
        id: 'strategist',
        name: 'Strategist',
        icon: 'sig_strategist',
        tooltip: 'A Strategist is a person who is often on the shortest paths between others and also connected to other influential people. They hold key strategic positions within the network.',
        criterion:0.90,
        color:'-blue'
      },
      {
        id: 'community_leader',
        name: 'Community Leader',
        icon: 'sig_communityleader',
        tooltip: 'A Community Leader is an individual who has strong, cohesive relationships with a large number of people. They are often seen as the focal point or leader within their communities.',
        criterion:0.90,
        color:'-blue'
      },
      {
        id: 'linker',
        name: 'Linker',
        icon: 'sig_link',
        tooltip: 'A Linker is a powerful bridge builder with considerable influence that ripples throughout the network. They serve as vital links between different communities within the network.',
        criterion:0.90,
        color:'-blue'
      },
      // New Signatures for Risks
      {
        id: 'peripheral',
        name: 'Peripheral',
        icon: 'sig_peripheral',
        tooltip: 'Peripheral indicates that an individual has very few direct connections within the network. This suggests limited integration and potential isolation, possibly missing out on information and collaborative opportunities.',
        criterion: 0.1,
        criterion_type: 'lte',
        data_field: 'connectivity',
        color: '-grey'
      },
      {
        id: 'distant',
        name: 'Distant',
        icon: 'sig_distant',
        tooltip: 'Distant indicates that an individual is far from others in the network on average. This suggests difficulty in efficiently spreading information or influence, potentially indicating isolation or a role on the network\'s edge.',
        criterion: 0.1,
        criterion_type: 'lte',
        data_field: 'reachability',
        color: '-grey'
      },
      //END FIX code before release
    ],
    statusTeam:false
    // iframeLink: '',
  },
  mutations: {
    setIframeLink(state: any, obj: any) {
      state.team_id = obj.team || ''
      state.company_id = obj.company
    },
    setTeamId(state: any, value: any) {
      state.team_id = value.keyword || ''
    },
    setNodes(state: any, value: string) {
      state.selected_nodes = value
    },
    setAttribute(state: any, value: string) {
      state.selected_attribute = value
    },
    setLinks(state: any, value: string) {
      state.selected_links = value
    },
    setSelectedEmail(state: any, value: string) {
      state.selected_email = value
    },
    setLoadingRelationshipsTabs(state: any, value: any) {
      state.loadingRelationshipsTabs = value
    },
    setOrganization1level(state: any, value: any) {
      state.organization1level = value
    },
    setDataAllAttribute(state: any, value: any) {
      state.dataAllAttribute = value
    },
    setTeamMembersIndex(state: any, value: any) {
      state.teamMembersIndex = value || {}
    },
    setDataList(state: any, value: any) {
      state.datalist = value || [];
    },
    setDataListAll(state: any, value: any) {
      state.datalistAll = value || [];
    },
    set_DataListAll(state: any, value: any) {
      state._datalistAll = value || [];
    },
    setSortField(state: any, result: any) {
      state.SortField = result
    },
    setSortOrder(state: any, result: any) {
      state.SortOrder = result
    },
    setPage(state: any, result: any) {
      state.page = result
    },
    setDataTableBuildToken(state: any, result: any) {
      state.dataTableBuildToken = result
    },
    setSelectTeam(state: any, result: any){
      state.statusTeam = result
    }
  },
  
  actions: {
    async getOrganization({ commit,state, rootState, dispatch }: any) {
      if(Object.keys(state.organization1level).length == 0){
        let organization = rootState.main.organization
        if(!organization || Object.keys(organization).length === 0){
          organization = await dispatch('main/getOrganization', null, { root: true })
        }

        let teamData = rootState.main.allTeam
        if((!teamData || Object.keys(teamData).length === 0) && rootState.team && rootState.team.teamsall && rootState.team.teamsall.length > 0){
          teamData = mapTeamsByKey(rootState.team.teamsall)
        }
        if(!teamData || Object.keys(teamData).length === 0){
          teamData = await getTeamByCompany(rootState.authen.user.company)
        }

        commit('setOrganization1level', normalizeOrganizationTree(organization, teamData))
      }
    },

    async getAttributeData({commit,dispatch,state,rootState}:any, user:any){
      let userMaster = rootState.main.allUser
      if(!userMaster || Object.keys(userMaster).length === 0) {
        userMaster = await dispatch('main/getuser', false, { root: true })
      }

      let teamMaster = rootState.main.allTeam
      if((!teamMaster || Object.keys(teamMaster).length === 0) && rootState.team && rootState.team.teamsall && rootState.team.teamsall.length > 0){
        teamMaster = mapTeamsByKey(rootState.team.teamsall)
      }
      const relMasterPromise = firebase.database().ref('rel_maps').orderByChild("comp_id").equalTo(user.company).once('value')
      if(!teamMaster || Object.keys(teamMaster).length === 0){
        teamMaster = await getTeamByCompany(user.company)
      }
      const relMaster = (await relMasterPromise).val() || {}
      let dataAllAttribute:any = {}
      const teamMembersIndex:any = {}
      const managementChainCache: Record<string, string[]> = {}
      const managementDistanceCache: Record<string, number> = {}
      /* set edges most valuable of rel_weight in the same node */
      const data_relweight:any={}
      _.forEach(relMaster, (item,keyId)=> {
        const userEmail = keyId.replace(/\|\|/g, ".");
        if(userMaster[keyId]){
          const edges = item.edges
          _.forEach(edges, (edges_i)=> {
            if(edges_i.node1 == userEmail || edges_i.node2 == userEmail){
              if(data_relweight[edges_i.node1+"_"+edges_i.node2] == undefined && data_relweight[edges_i.node2+"_"+edges_i.node1] == undefined){
                data_relweight[edges_i.node1+"_"+edges_i.node2] = edges_i.rel_weight
              }else{
                if(data_relweight[edges_i.node1+"_"+edges_i.node2] && data_relweight[edges_i.node1+"_"+edges_i.node2] < edges_i.rel_weight){
                  data_relweight[edges_i.node1+"_"+edges_i.node2] = edges_i.rel_weight
                }else if(data_relweight[edges_i.node2+"_"+edges_i.node1] && data_relweight[edges_i.node2+"_"+edges_i.node1] < edges_i.rel_weight){
                  data_relweight[edges_i.node2+"_"+edges_i.node1] = edges_i.rel_weight
                }
              }
            }
          });
        }
      });
      /* End set edges most valuable of rel_weight in the same node */
      _.forEach(relMaster, (item,keyId)=> {
        const userEmail = keyId.replace(/\|\|/g, ".");
        if(userMaster[keyId]){
          const userObj = Object()
          userObj.firstname = item.firstname
          userObj.lastname = item.lastname
          userObj.fullName = item.firstname + " " + item.lastname
          userObj.cuttingName = item.firstname.charAt(0)+item.lastname.charAt(0)
          userObj.email = userEmail
          userObj.emailKey = keyId
          userObj.profileImage = item.pic
          userObj.boss = (userMaster[keyId].boss) ? userMaster[keyId].boss : ''
          userObj.teamId = (userMaster[keyId].team) ? userMaster[keyId].team : ''
          userObj.teamName = (userMaster[keyId].team&&teamMaster[userMaster[keyId].team]) ? teamMaster[userMaster[keyId].team].name : ''
          // userObj.teamName = _.get(userMaster, `teamMaster[${userMaster[keyId].team}].name`, "")
          userObj.inner_count = 0
          userObj.outer_count = 0
          userObj.strong_count = 0
          userObj.total_count = 0
          userObj.innerMembers = []
          userObj.outerMembers = []
          userObj.strongMembers = []
          userObj.totalMembers = []
          userObj.me_ratio = -1
          userObj.signatures_count = 0
          if(state.signatures.length > 0){
            _.forEach(state.signatures, (signature)=> {
              const dataFieldId = signature.data_field || signature.id;
              const value = item[dataFieldId];
              let meetsCriterion = false;

              if (value !== undefined && value !== null) {
                if (signature.criterion_type === 'lte') {
                  // Handle <= criteria for Peripheral and Distant
                  if (signature.criterion !== undefined && value <= signature.criterion) {
                    meetsCriterion = true;
                  }
                } else {
                  // Handle default >= criteria
                  if (signature.criterion !== undefined && value >= signature.criterion) {
                    meetsCriterion = true;
                  } 
                  // Handle the specific 0.85 threshold case (adjust if needed)
                  else if (signature.criterion === undefined && value >= 0.85) {
                     // Existing logic for signatures without explicit criterion but >= 0.85
                     // Check if this specific rule (>= 0.85) should apply only to certain signatures
                     // For now, keeping it as is, but might need refinement based on exact requirements
                     // Example: Check signature.id if this rule is specific
                     if (['brokerage', 'cohesion', 'connectivity', 'collective', 'influence', 'reachability', 'influenceI', 'influenceII', 'ripple'].includes(signature.id)) {
                        meetsCriterion = true;
                     }
                  }
                }
              }

              if (meetsCriterion) {
                if(userObj.signatures == undefined){
                  userObj.signatures = []
                }
                // Ensure signature object has color property before pushing
                const signatureToAdd = { ...signature };
                if (!signatureToAdd.color) {
                   signatureToAdd.color = ''; // Default color if missing
                }
                userObj.signatures.push(signatureToAdd)
                userObj.signatures_count += 1
              }
            });
          }
          const edges = item.edges
          const nodeCheck:any=[]
          _.forEach(edges, (edges_i)=> {
            if(edges_i.node1 == userEmail || edges_i.node2 == userEmail){
              if(nodeCheck[edges_i.node1+"_"+edges_i.node2] == undefined && nodeCheck[edges_i.node2+"_"+edges_i.node1] == undefined){
                nodeCheck[edges_i.node1+"_"+edges_i.node2] = true
                if(edges_i.node1 == userEmail){
                  const keyIdNode2 = edges_i.node2.replace(/\./g, "||")
                  if(userMaster[keyIdNode2]){
                    // Use management distance instead of org chart hierarchy
                    const managementDistance = findManagementDistance(keyId, keyIdNode2, userMaster, managementChainCache, managementDistanceCache)
                    if(managementDistance <= 2){
                      userObj.inner_count += 1
                      userObj.innerMembers.push(keyIdNode2)
                    }else{
                      userObj.outer_count += 1
                      userObj.outerMembers.push(keyIdNode2)
                    }
                    if((data_relweight[edges_i.node1+"_"+edges_i.node2] && data_relweight[edges_i.node1+"_"+edges_i.node2] >= 0.66) || (data_relweight[edges_i.node2+"_"+edges_i.node1] && data_relweight[edges_i.node2+"_"+edges_i.node1] >= 0.66)){
                      userObj.strong_count += 1
                      userObj.strongMembers.push(keyIdNode2)
                    }
                    userObj.totalMembers.push(keyIdNode2)
                  }
                }else if(edges_i.node2 == userEmail){
                  const keyIdNode1 = edges_i.node1.replace(/\./g, "||")
                  if(userMaster[keyIdNode1]){
                    // Use management distance instead of org chart hierarchy
                    const managementDistance = findManagementDistance(keyId, keyIdNode1, userMaster, managementChainCache, managementDistanceCache)
                    if(managementDistance <= 2){
                      userObj.inner_count += 1
                      userObj.innerMembers.push(keyIdNode1)
                    }else{
                      userObj.outer_count += 1
                      userObj.outerMembers.push(keyIdNode1)
                    }
                    if((data_relweight[edges_i.node1+"_"+edges_i.node2] && data_relweight[edges_i.node1+"_"+edges_i.node2] >= 0.66) || (data_relweight[edges_i.node2+"_"+edges_i.node1] && data_relweight[edges_i.node2+"_"+edges_i.node1] >= 0.66)){
                      userObj.strong_count += 1
                      userObj.strongMembers.push(keyIdNode1)
                    }
                    userObj.totalMembers.push(keyIdNode1)
                  }
                }
              }
            }
          });
          userObj.total_count = userObj.inner_count + userObj.outer_count
          
          // if(userObj.total_count > 3){
            dataAllAttribute[keyId] = {}
            Object.assign(dataAllAttribute[keyId], userObj);
            if(userObj.teamId){
              if(!teamMembersIndex[userObj.teamId]){
                teamMembersIndex[userObj.teamId] = []
              }
              teamMembersIndex[userObj.teamId].push(dataAllAttribute[keyId])
            }
          // }
        }
      });
      /* Add M/E Ratio to dataAllAttribute */
      _.forEach(dataAllAttribute, (item,keyId)=> {
        if(item.boss){
          const keyIdBoss = item.boss.replace(/\./g, "||")
          if(dataAllAttribute[keyIdBoss] && dataAllAttribute[keyIdBoss].total_count > 0 && dataAllAttribute[keyId].total_count > 0){
            dataAllAttribute[keyId].me_ratio = globalfunc.round(dataAllAttribute[keyIdBoss].total_count / dataAllAttribute[keyId].total_count, 1)
          }
        }
      });

      // Compute power rating once per user
      _.forEach(dataAllAttribute, (item, keyId) => {
        dataAllAttribute[keyId].powerRating = calculatePowerRating(item);
      });
      /* Add arrData to child */
      _.forEach(dataAllAttribute, (item,keyId)=> {
        if(dataAllAttribute[keyId].totalArrData == undefined){
          dataAllAttribute[keyId].totalArrData = []
        }
        if(item.innerMembers.length > 0){
          dataAllAttribute[keyId].innerArrData = []
          _.forEach(item.innerMembers, (itemInnerMember)=> {
            if(dataAllAttribute[itemInnerMember]){
              dataAllAttribute[keyId].innerArrData.push(dataAllAttribute[itemInnerMember])
              dataAllAttribute[keyId].totalArrData.push(dataAllAttribute[itemInnerMember])
            }
          });
          dataAllAttribute[keyId].innerArrData = _.orderBy(dataAllAttribute[keyId].innerArrData, ['total_count','fullName'], ['desc','asc']);
        }
        if(item.outerMembers.length > 0){
          dataAllAttribute[keyId].outerArrData = []
          _.forEach(item.outerMembers, (itemOuterMember)=> {
            if(dataAllAttribute[itemOuterMember]){
              dataAllAttribute[keyId].outerArrData.push(dataAllAttribute[itemOuterMember])
              dataAllAttribute[keyId].totalArrData.push(dataAllAttribute[itemOuterMember])
            }
          });
          dataAllAttribute[keyId].outerArrData = _.orderBy(dataAllAttribute[keyId].outerArrData, ['total_count','fullName'], ['desc','asc']);
        }
        if(item.strongMembers.length > 0){
          dataAllAttribute[keyId].strongArrData = []
          _.forEach(item.strongMembers, (itemStrongMember)=> {
            if(dataAllAttribute[itemStrongMember]){
              dataAllAttribute[keyId].strongArrData.push(dataAllAttribute[itemStrongMember])
            }
          });
          dataAllAttribute[keyId].strongArrData = _.orderBy(dataAllAttribute[keyId].strongArrData, ['total_count','fullName'], ['desc','asc']);
        }
        dataAllAttribute[keyId].totalArrData = _.orderBy(dataAllAttribute[keyId].totalArrData, ['total_count','fullName'], ['desc','asc']);
      });
      _.forEach(teamMembersIndex, (members, teamId) => {
        teamMembersIndex[teamId] = _.orderBy(members, ['total_count','fullName'], ['desc','asc']);
      });
      const dataAllAttributeList = _.orderBy(Object.values(dataAllAttribute), ['total_count','fullName'], ['desc','asc']);
      commit("setTeamMembersIndex", teamMembersIndex)
      commit("setDataAllAttribute", dataAllAttributeList)
      dispatch("getDataTable", state.team_id || "")
    },
    async getDataTable({commit,state,rootState}:any, teamId:any){
      const buildToken = Date.now()
      commit('setDataTableBuildToken', buildToken)
      commit('setPage', 1)
      const organization1level = state.organization1level || {}
      const dataAllAttribute = Array.isArray(state.dataAllAttribute) ? state.dataAllAttribute : []
      const isAllTeamsSelection = state.team_id && typeof state.team_id === 'string' && state.team_id.search(/all teams/i) == 0

      if(rootState.authen.user.user_type == 'leadership'){
        if(teamId == "" || isAllTeamsSelection){
          teamId = rootState.authen.user.team
        }
      }

      let result:Array<any> = []
      if(teamId && !isAllTeamsSelection){
        const scopedTeams = organization1level[teamId] || { [teamId]: true }
        commit('setSelectTeam', true)
        _.forEach(scopedTeams, (_item,keyId)=> {
          const teamData:any = state.teamMembersIndex[keyId] || []
          if(teamData.length > 0){
            result.push(...teamData)
          }
        });
      }else{
        commit('setSelectTeam', false)
        result = dataAllAttribute.slice()
      }

      let field = state.SortField
      let order = state.SortOrder
      
      if (order === "ascending") {
        order = 'asc'
      } else {
        order = 'desc'
      }
      if (field === "") {
        field = "total_count"
      }
      
      // For large datasets, defer sorting to prevent blocking
      if (result.length > 500) {
        // Store unsorted data first to show something quickly
        commit('setDataList', result.slice(0, state.size_page));
        commit('setDataListAll', result);
        commit('set_DataListAll', result);
        
        // Then sort asynchronously
        setTimeout(() => {
          if (state.dataTableBuildToken !== buildToken) {
            return
          }
          const sortedResult = _.orderBy(result, [field,'fullName'], [order,'asc']);
          commit('setDataList', sortedResult.slice(0, state.size_page));
          commit('setDataListAll', sortedResult);
          commit('set_DataListAll', sortedResult);
        }, 0);
      } else {
        // For smaller datasets, sort immediately
        result = _.orderBy(result, [field,'fullName'], [order,'asc']);
        if (state.dataTableBuildToken !== buildToken) {
          return
        }
        commit('setDataList', result.slice(0, state.size_page));
        commit('setDataListAll', result);
        commit('set_DataListAll', result);
      }
   
    },
    currentChange({ commit, state }: any, page: any) {
      let size = state.size_page
      let start_row = (page - 1) * size
      let end_row = ((page - 1) * size) + size
      let results: Array<any> = [];
      results = state.datalistAll
      let field = state.SortField
      let order = state.SortOrder
      
      if (order === "ascending") {
        order = 'asc'
      } else {
        order = 'desc'
      }
      if (field === "") {
        field = "total_count"
      }
      results = _.orderBy(results, [field,'fullName'], [order,'asc']);
      commit('setPage', page)
      commit('setDataList', results.slice(start_row, end_row))
    },
    sort({ commit, state }: any, value: any) {
      let results: Array<any> = [];
      results = state.datalistAll
      let size = state.size_page
      let page = state.page
      let start_row = (page-1) * size
      let end_row = ((page-1) * size)+size
      let field = value[0]
      let order = value[1]
      
      if (order === "ascending") {
        order = 'asc'
      } else {
        order = 'desc'
      }
      if (field === "") {
        field = "total_count"
      }
      let _users
      _users = _.orderBy(results, [field,'fullName'], [order,'asc']);
      commit('setSortField', field)
      commit('setSortOrder', order)
      commit('setDataList', _users.slice(start_row,end_row))
    },
    filter({ commit, state }: any, value: any) {
      commit('setPage', 1)
      let results: Array<any> = [];
      // const teamId=value.teamId
      // const organization1level = state.organization1level
      // console.log("value",value)
      if(state.statusTeam==true){
        results = state._datalistAll

      }else{
        results = state.dataAllAttribute
      }

      let size = state.size_page
      let page = state.page
      let start_row = (page-1) * size
      let end_row = ((page-1) * size)+size
      let relation:Array<any>=[]
      // console.log("value",value)
      // console.log("results",results)
      if(value.length>0){
         relation=_.filter(results, function (s) {
          return _.some(s.signatures, function (o) {
            return _.includes(value, o.name);
          });
        });

      }else{
        relation=results
      }
    // console.log("relation",relation)
      commit('setDataList', relation.slice(start_row,end_row))
      commit('setDataListAll', relation);
      
    },
  },
  getters: {
    organization1level: (state: any) => {
      return state.organization1level
    },
    loadingRelationshipsTabs: (state: any) => {
      return state.team_id
    },
    selectTeam: (state: any) => {
      return state.team_id
    },
    iframeLink: (state: any, _getters: any, _rootState: any, rootGetters: any) => {
      // console.log("iframeLink()");
      const api = "https://testdrive.happily.ai";
      // const api = window.location.hostname == "localhost" ? "http://localhost:8081" : "https://testdrive.happily.ai";
      const path = "/relationships-network?";
      if (state.company_id) {
        const company = "company=" + state.company_id;
        const team = (state.team_id && typeof state.team_id === 'string' && state.team_id.search(/all teams/i) < 0) ? "&team=" + state.team_id : '';
        const nodes = state.selected_nodes ? "&nodes=" + state.selected_nodes : '';
        const attribute = state.selected_attribute ? "&attribute=" + state.selected_attribute : '';
        const links = state.selected_links ? "&links=" + state.selected_links : '';
        const email = state.selected_email ? "&email=" + encodeURIComponent(state.selected_email) : '';
        // Include blur parameter when demo mode blur is enabled
        const blur = (rootGetters && rootGetters["demomode/blurMode"]) ? "&blur=true" : '';
        const mode = "&mode=window"
        const URL = api + path + company + team + nodes + attribute + links + email + blur + "&v=2";
        // console.log("Open link below to see relationships map for fullscreen version!");
        //console.log(URL + mode);
        return URL
      } else {
        return "";
      }
    },
    datalistAll: (state: any) => {
      return state.datalistAll
    },
    datalist: (state: any) => {
      return state.datalist
    },
    page : (state:any) => {
      return state.page
    },
    signatures : (state:any) => {
      return state.signatures
    },
  }
}
